import {useEffect, useMemo, useState} from 'react';
import AurButton from '../../../common/aur-button/AurButton';
import {useNavigate} from 'react-router-dom';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as BackArrow} from '../../../../assests/svg/back-arrow-icon.svg';
import AurServiceAgreementDropdown from '../components/AurServiceAgreementDropdown';
import HotLeadsCard from './components/HotLeadsCard';
import ServiceAgreementCard from './components/ServiceAgreementCard';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setServiceAgreementList,
  storeSelectedAgreement,
} from '../../../../redux/slices/projectCreationSlice';
import navConstants from '../../../../constants/navConstants';
import {
  CLIENT_PERMISSION_CODES_CONSTANTS,
  IS_HOT_LEAD_FORM_VISIBLE,
} from '../../../../constants/constants';
import AurBanner from '../../../common/aur-banner/AurBanner';
import {getBillingEngagementsList} from '../../../../api/ClientManagementService';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {useHasClientPermission} from '../../../../hooks/permissions/usePermissionsByClientId';

const ServiceAgreement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {serviceAgreementList, selectedAgreement, clientId} = useSelector(
    (state: RootState) => state.projectCreation
  );

  const hasCreateProjectPermission = useHasClientPermission(
    clientId ?? '',
    CLIENT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT
  );

  const [isListApiLoading, setIsListApiLoading] = useState(false);
  const [apiError, setApiError] = useState<boolean>(false);

  const stringPath = 'projectCreation.serviceAgreementSelectionPage.';

  // To get list by clientId and update the local store
  const getBillingEngagements = async () => {
    setIsListApiLoading(true);
    await getBillingEngagementsList(clientId ?? '')
      .then((res) => {
        const response = res.data?.data || [];
        dispatch(setServiceAgreementList(response));
      })
      .catch(() => {
        setApiError(true);
      })
      .finally(() => {
        setIsListApiLoading(false);
      });
  };

  useEffect(() => {
    if (!clientId) {
      navigate('/welcome-multi-client', {replace: true});
      return;
    }

    if (!hasCreateProjectPermission) {
      navigate('/secure-page', {replace: true});
      return;
    }
    window.scrollTo(0, 0);
    getBillingEngagements();
  }, []);

  const bannerMessage = useMemo(() => {
    if (serviceAgreementList?.length == 0)
      return t(stringPath + 'infoBanner.noAvailableAgreements');
    if (apiError) return t(stringPath + 'infoBanner.agreementsFailedToLoad');
    return null;
  }, [serviceAgreementList, apiError]);

  const goBack = () => navigate(-1);

  const handleServiceAgreementSelectionChange = (selectedId: string) => {
    if (serviceAgreementList?.filter((data) => data.id === selectedId)[0]) {
      dispatch(
        storeSelectedAgreement(
          serviceAgreementList.filter((data) => data.id === selectedId)[0]
        )
      );
    }
  };

  const getHotLeadsLaunchCardTitle = () => {
    return serviceAgreementList.length < 1
      ? t(stringPath + 'hotLeadsLaunchCard.titleWithoutAggrementList')
      : t(stringPath + 'hotLeadsLaunchCard.titleWithAggrementList');
  };

  const getHotLeadsLaunchCardSubTitle = () => {
    return serviceAgreementList.length < 1 || selectedAgreement
      ? t(stringPath + 'hotLeadsLaunchCard.descriptionWithoutAggrementList')
      : t(stringPath + 'hotLeadsLaunchCard.descriptionWithAggrementList');
  };

  return (
    <Box className="service-agreement">
      <Box className="service-agreement-top-bg-container">
        <div className="left-side-glow">
          <div className="left-side-div" />
        </div>
        <div className="right-side-glow">
          <div className="right-side-div" />
        </div>
        <Box className="service-agreement-top-container">
          <AurButton
            dataTestId={'hotLeadsBackButton'}
            classNames={
              'typo-text buttonMedium_size service-agreement-back-button'
            }
            type={'text'}
            size={'Medium'}
            startIcon={<BackArrow />}
            label={t('buttons.back')}
            onClick={goBack}
            tabIndex={0}
          />
        </Box>
        <Box className="service-agreement-middle-container">
          <Typography className="typo-text headline1_size service-agreement-title">
            {t(stringPath + 'title')}
          </Typography>
          <Typography className="typo-text body2_size service-agreement-content">
            {t(stringPath + 'subTitle')}
          </Typography>
          {!isListApiLoading && bannerMessage && (
            <AurBanner errorText={bannerMessage} errorType="warning" showIcon />
          )}
          <Box
            className={
              bannerMessage
                ? 'service-agreement-dropdown'
                : 'service-agreement-dropdown no-banner-spacing'
            }
          >
            <AurServiceAgreementDropdown
              options={serviceAgreementList}
              value={selectedAgreement?.id}
              onChange={handleServiceAgreementSelectionChange}
              keepDisabledFocusable
            />
            <AurButton
              classNames="service-agreement-submit-button"
              label={t('buttons.continue')}
              type="continue"
              size="Large"
              disabled={!selectedAgreement}
              keepDisabledFocusable
              dataTestId="serviceAgreementSubmit"
              onClick={() => {
                navigate(navConstants.projectCreation.createProject, {
                  replace: true,
                });
              }}
            />
          </Box>
          {selectedAgreement && (
            <Box className="service-agreement-selected-card">
              <ServiceAgreementCard {...selectedAgreement} />
            </Box>
          )}
          {IS_HOT_LEAD_FORM_VISIBLE && (
            <>
              {serviceAgreementList.length > 0 && (
                <div className="separator">
                  <span className="line"></span>
                  <span className="text">or</span>
                  <span className="line"></span>
                </div>
              )}
              <Box className="service-agreement-go-to-hot-leads-section">
                <HotLeadsCard
                  title={getHotLeadsLaunchCardTitle()}
                  description={getHotLeadsLaunchCardSubTitle()}
                  onClick={() => {
                    navigate(navConstants.projectCreation.hotLeadsForm);
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <AurLoadingIndicator isOpen={isListApiLoading} overlay />
    </Box>
  );
};

export default ServiceAgreement;
